import { Alert, Button, Paper, Stack, Typography } from '@mui/material'
import type {
  PostTaskSchedulesSimulateResponse,
  TaskScheduleSimulatedTask,
} from '@ulysses-inc/harami_api_client'
import { format } from 'date-fns'
import type { FC } from 'react'
import { readback } from '../form/readback'
import {
  type ClientErrors,
  type TaskScheduleForm,
  isSingleDayPeriod,
} from '../form/taskScheduleForm'

type Props = {
  form: TaskScheduleForm
  /** フォームの判定で見つかった不正。あれば読み上げを警告に変える */
  clientErrors: ClientErrors
  /** 最後に「試算する」を押したときの結果。押す前は undefined */
  simulation: PostTaskSchedulesSimulateResponse | undefined
  /** 試算してから入力が変わった（結果が今の入力と合っていない） */
  isStale: boolean
  canSimulate: boolean
  isFetching: boolean
  isError: boolean
  onSimulate: () => void
}

const WEEKDAY_JA = ['日', '月', '火', '水', '木', '金', '土']

/** YYYY-MM-DD を「2026/09/21（月）」にする。暦日の文字列なので時計は使わない */
const dateLabel = (isoDate: string) => {
  const [y, m, d] = isoDate.split('-').map(Number)
  const date = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1)
  return `${format(date, 'yyyy/MM/dd')}（${WEEKDAY_JA[date.getDay()]}）`
}

/** instant（UTC）を端末の時計で「09:00」にする。管理画面の利用者は日本にいる前提（TZ は Asia/Tokyo 固定） */
const timeLabel = (at: Date) => format(at, 'HH:mm')

const rangeLabel = (task: TaskScheduleSimulatedTask, singleDay: boolean) =>
  singleDay
    ? `${timeLabel(task.startAt)} 〜 ${
        format(task.dueAt, 'yyyy-MM-dd') === task.startDate ? '' : '翌 '
      }${timeLabel(task.dueAt)}`
    : `${format(new Date(task.startDate), 'MM/dd')} 0:00 〜 ${dateLabel(task.endDate)} 24:00`

/**
 * 右側の「設定の意味」と「この設定でできるタスク」。
 * 読み上げ文はフォームの値から組む。タスクの一覧は「試算する」を押したときの試算 API の結果を描く。
 */
export const SimulationPanel: FC<Props> = ({
  form,
  clientErrors,
  simulation,
  isStale,
  canSimulate,
  isFetching,
  isError,
  onSimulate,
}) => {
  const hasClientErrors = Object.keys(clientErrors).length > 0
  const rejected =
    hasClientErrors || (simulation !== undefined && !simulation.accepted)
  const singleDay = isSingleDayPeriod(form)

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderColor: rejected ? 'error.main' : 'primary.main',
          bgcolor: rejected ? 'error.50' : 'primary.50',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          設定の意味
        </Typography>
        <Typography variant="h6" component="p">
          {rejected
            ? '入力に直すところがあります。赤い文を確認してください。'
            : readback(form)}
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" fontWeight="bold">
            この設定でできるタスク
          </Typography>
          <Button
            id="task-schedule-simulate"
            variant="outlined"
            size="small"
            disabled={!canSimulate || isFetching}
            onClick={onSimulate}
          >
            {isFetching ? '計算中…' : '試算する'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          直近 6 件のタスクの予定です
        </Typography>
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {simulation === undefined && !isFetching && !isError && (
            <Typography variant="body2" color="text.secondary">
              {canSimulate
                ? '「試算する」を押すと、ここにタスクの予定が出ます'
                : '件名・ひな形・対象現場を入れて「試算する」を押すと、ここにタスクの予定が出ます'}
            </Typography>
          )}
          {isStale && simulation !== undefined && (
            <Alert severity="info">
              入力が変わりました。もう一度「試算する」を押すと今の入力で計算します
            </Alert>
          )}
          {isError && (
            <Alert severity="error">
              試算に失敗しました。時間をおいて入力し直してください。
            </Alert>
          )}
          {simulation !== undefined &&
            !simulation.accepted &&
            simulation.errors.map(error => (
              <Alert key={`${error.field}:${error.message}`} severity="error">
                <Typography
                  variant="caption"
                  component="span"
                  fontWeight="bold"
                >
                  {error.field}
                </Typography>
                {'　'}
                {error.message}
              </Alert>
            ))}
          {simulation?.accepted && simulation.tasks.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              この設定ではタスクができません（適用期間内に開始日が無い）
            </Typography>
          )}
          {simulation?.accepted &&
            simulation.tasks.map(task => (
              <Stack
                key={task.startDate}
                direction="row"
                justifyContent="space-between"
                sx={{
                  px: 1,
                  py: 0.75,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  opacity: isStale ? 0.5 : 1,
                }}
              >
                <Typography variant="body2" fontWeight="bold">
                  {dateLabel(task.startDate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rangeLabel(task, singleDay)}
                </Typography>
              </Stack>
            ))}
        </Stack>
      </Paper>
    </Stack>
  )
}
